import { useState, useEffect } from "react";
import { Settings, X, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";

const AlertSettings = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    extremeFearThreshold: 25,
    extremeGreedThreshold: 75,
    notificationFrequency: 30, // minutes
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("alertSettings");
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (err) {
        console.error("Error loading alert settings:", err);
      }
    }
  }, []);

  // Save settings to localStorage
  const saveSettings = () => {
    localStorage.setItem("alertSettings", JSON.stringify(settings));
    toast.success("Alert preferences saved!");
    setIsOpen(false);
  };

  const resetToDefaults = () => {
    const defaults = {
      enabled: true,
      extremeFearThreshold: 25,
      extremeGreedThreshold: 75,
      notificationFrequency: 30,
    };
    setSettings(defaults);
    localStorage.setItem("alertSettings", JSON.stringify(defaults));
    toast.success("Reset to default settings");
  };

  return (
    <>
      {/* Market Alerts Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="glass-card p-3 border-white/10 rounded-lg hover:bg-white/5 transition-all"
        title="Market Alerts"
      >
        <Bell className="w-5 h-5" />
      </button>

      {/* Settings Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <div className="glass-card p-5 rounded-xl border border-white/20 w-full max-w-md mx-4 my-auto shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#FFD600]" />
                <h2 className="text-xl font-bold heading-font">Alert Preferences</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-muted-foreground hover:text-white transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Settings Form */}
            <div className="space-y-4">
              {/* Enable/Disable Alerts */}
              <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/5">
                <div className="flex items-center gap-3">
                  {settings.enabled ? (
                    <Bell className="w-5 h-5 text-[#00FF94]" />
                  ) : (
                    <BellOff className="w-5 h-5 text-[#FF0055]" />
                  )}
                  <div>
                    <div className="font-semibold text-sm">Alerts {settings.enabled ? "Enabled" : "Disabled"}</div>
                    <div className="text-xs text-white/70">
                      {settings.enabled ? "You will receive extreme market alerts" : "All alerts are muted"}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                    settings.enabled
                      ? "bg-[#00FF94] text-black"
                      : "bg-white/10 text-white"
                  }`}
                >
                  {settings.enabled ? "ON" : "OFF"}
                </button>
              </div>

              {/* Extreme Fear Threshold */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF0055]"></span>
                  Extreme Fear Threshold
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="10"
                    max="40"
                    step="1"
                    value={settings.extremeFearThreshold}
                    onChange={(e) => setSettings({ ...settings, extremeFearThreshold: parseInt(e.target.value) })}
                    className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    disabled={!settings.enabled}
                  />
                  <span className="text-lg font-bold text-[#FF0055] w-10 text-right">
                    {settings.extremeFearThreshold}
                  </span>
                </div>
                <p className="text-xs text-white/70">
                  Alert when F&G Index ≤ {settings.extremeFearThreshold} (Strong buying opportunity)
                </p>
              </div>

              {/* Extreme Greed Threshold */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#00FF94]"></span>
                  Extreme Greed Threshold
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="60"
                    max="90"
                    step="1"
                    value={settings.extremeGreedThreshold}
                    onChange={(e) => setSettings({ ...settings, extremeGreedThreshold: parseInt(e.target.value) })}
                    className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    disabled={!settings.enabled}
                  />
                  <span className="text-lg font-bold text-[#00FF94] w-10 text-right">
                    {settings.extremeGreedThreshold}
                  </span>
                </div>
                <p className="text-xs text-white/70">
                  Alert when F&G Index ≥ {settings.extremeGreedThreshold} (Consider taking profits)
                </p>
              </div>

              {/* Notification Frequency */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FFD600]"></span>
                  Check Frequency
                </label>
                <select
                  value={settings.notificationFrequency}
                  onChange={(e) => setSettings({ ...settings, notificationFrequency: parseInt(e.target.value) })}
                  className="w-full p-2.5 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:border-primary focus:outline-none"
                  disabled={!settings.enabled}
                >
                  <option value="5">Every 5 minutes</option>
                  <option value="15">Every 15 minutes</option>
                  <option value="30">Every 30 minutes</option>
                  <option value="60">Every hour</option>
                </select>
                <p className="text-xs text-white/70">
                  How often to check for new alerts
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-5">
              <Button
                onClick={() => setIsOpen(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={resetToDefaults}
                variant="outline"
                className="flex-1"
              >
                Reset to Defaults
              </Button>
              <Button
                onClick={saveSettings}
                className="flex-1 bg-primary text-primary-foreground"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlertSettings;
